import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Home, LayoutDashboard, List, Map, User, LogIn, LogOut, Menu, X, Globe, Shield, MessageCircle } from 'lucide-react';
import NotificationBell from './NotificationBell';

export default function Navigation() {
  const { user, logout } = useAuth();
  const { t, lang, switchLang } = useLanguage();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);

  const LANGUAGES = [
    { code: 'it', label: 'IT', flag: 'Italiano' },
    { code: 'en', label: 'EN', flag: 'English' },
    { code: 'fr', label: 'FR', flag: 'Français' },
    { code: 'zh', label: '中文', flag: '中文' },
    { code: 'ru', label: 'RU', flag: 'Русский' },
  ];

  const isActive = (path) => location.pathname === path;

  const navItems = [
    { path: '/', label: t('home'), icon: Home },
    { path: '/dashboard', label: t('dashboard'), icon: LayoutDashboard },
    { path: '/richieste', label: t('richieste'), icon: List },
    { path: '/le-mie-richieste', label: 'Le Mie', icon: MessageCircle },
    { path: '/mappa', label: t('mappa'), icon: Map },
    ...(user && user.role === 'admin' ? [{ path: '/admin', label: 'Admin', icon: Shield }] : []),
  ];

  const linkClass = (path) =>
    `flex items-center gap-2 px-3 py-2 text-sm uppercase tracking-widest font-bold transition-all border-b-2 ${
      isActive(path)
        ? 'border-[#FF3B30] text-[#0D1B2A]'
        : 'border-transparent text-[#4A6B8A] hover:text-[#0D1B2A] hover:border-[#FF3B30]/50'
    }`;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-[#B8D4E8]" data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_help-beacon-9/artifacts/ok1e76vb_17%20apr%202026%2C%2009_06_17.png"
              alt="LIFE LINKE"
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navItems.map(({ path, label, icon: Icon }) => (
              <Link key={path} to={path} className={linkClass(path)} data-testid={`nav-${path.slice(1) || 'home'}`}>
                <Icon size={16} />
                <span className="text-xs">{label}</span>
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-3">
            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="flex items-center gap-1 px-2 py-1 text-xs uppercase tracking-widest text-[#4A6B8A] hover:text-[#0D1B2A] border border-[#B8D4E8] hover:border-[#FF3B30] transition-all"
                data-testid="lang-switcher"
              >
                <Globe size={14} />
                {LANGUAGES.find(l => l.code === lang)?.label || lang.toUpperCase()}
              </button>
              {langMenuOpen && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-[#B8D4E8] z-50 min-w-[140px]" data-testid="lang-menu">
                  {LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => { switchLang(l.code); setLangMenuOpen(false); }}
                      className={`w-full text-left px-3 py-2 text-xs uppercase tracking-widest transition-all flex items-center justify-between ${
                        lang === l.code ? 'text-[#FF3B30] bg-[#FF3B30]/10' : 'text-[#4A6B8A] hover:text-[#0D1B2A] hover:bg-[#E8F4FD]'
                      }`}
                      data-testid={`lang-option-${l.code}`}
                    >
                      <span>{l.flag}</span>
                      <span className="text-[10px]">{l.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {user && user !== false ? (
              <div className="flex items-center gap-3">
                <NotificationBell />
                <Link to="/profilo" className={linkClass('/profilo')} data-testid="nav-profilo">
                  <User size={16} />
                  <span className="text-xs">{user.nome || t('profilo')}</span>
                </Link>
                <button
                  onClick={logout}
                  className="flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-widest text-[#4A6B8A] hover:text-[#FF3B30] transition-all"
                  data-testid="logout-btn"
                >
                  <LogOut size={14} />
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="flex items-center gap-1 px-4 py-2 bg-[#FF3B30] text-[#0D1B2A] text-xs font-bold uppercase tracking-widest hover:bg-[#FF1A1A] transition-all"
                data-testid="login-link"
              >
                <LogIn size={14} />
                {t('login')}
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden text-[#0D1B2A] p-2"
            data-testid="mobile-menu-btn"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-[#B8D4E8] py-4 px-4" data-testid="mobile-menu">
          {navItems.map(({ path, label, icon: Icon }) => (
            <Link
              key={path}
              to={path}
              onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 text-sm uppercase tracking-widest font-bold ${
                isActive(path) ? 'text-[#FF3B30]' : 'text-[#4A6B8A]'
              }`}
            >
              <Icon size={18} />
              {label}
            </Link>
          ))}
          <div className="border-t border-[#B8D4E8] mt-3 pt-3 px-4">
            <div className="flex flex-wrap gap-2 mb-3">
              {LANGUAGES.map((l) => (
                <button
                  key={l.code}
                  onClick={() => { switchLang(l.code); }}
                  className={`px-3 py-1.5 text-xs uppercase tracking-widest border transition-all ${
                    lang === l.code ? 'border-[#FF3B30] text-[#FF3B30] bg-[#FF3B30]/10' : 'border-[#B8D4E8] text-[#4A6B8A]'
                  }`}
                  data-testid={`lang-mobile-${l.code}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3">
            {user && user !== false ? (
              <button onClick={() => { logout(); setMobileOpen(false); }} className="text-[#4A6B8A] text-xs uppercase tracking-widest">
                {t('esci')}
              </button>
            ) : (
              <Link to="/login" onClick={() => setMobileOpen(false)} className="text-[#FF3B30] text-xs font-bold uppercase tracking-widest">
                {t('login')}
              </Link>
            )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
